import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Crown } from "lucide-react";

const short = (w) => (w ? `${w.slice(0, 4)}…${w.slice(-4)}` : "anon");
const ago = (ts) => {
  const s = Math.max(1, Math.floor(Date.now() / 1000 - (ts || 0)));
  if (s < 3600) return `${Math.floor(s / 60) || 1}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/** Backer Wall — social proof: every wallet that tipped this coin. */
export default function BackerWall({ coin }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/coins/${coin.slug}/backers`).then((r) => setData(r.data)).catch(() => setData({ top: [], recent: [], total_backers: 0 }));
  }, [coin.slug]);

  if (!data) return null;

  return (
    <section className="border border-[#3a3a3a] bg-[#121212] p-5 mb-6" data-testid="backer-wall">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-1 flex items-center gap-2">
            <Users size={11} /> Backer wall
          </div>
          <h2 className="font-display font-black uppercase tracking-tight text-lg md:text-lg">
            {data.total_backers > 0 ? (
              <><span className="text-[#cfff04]" data-testid="backer-count">{data.total_backers}</span> wallet{data.total_backers === 1 ? "" : "s"} backed ${coin.ticker}</>
            ) : (
              <>Nobody has backed ${coin.ticker} yet.</>
            )}
          </h2>
        </div>
        {data.total_tips > 0 && (
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">{data.total_tips} tips total</span>
        )}
      </div>

      {data.top.length === 0 ? (
        <p className="text-sm text-[#d0d0d0]" data-testid="backer-empty">
          Be the first name on the wall — tip in SOL below and your wallet shows here forever.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="backer-top">
            {data.top.map((b, i) => (
              <a
                key={b.wallet}
                href={`https://solscan.io/account/${b.wallet}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`backer-${i}`}
                className={`border px-3 py-2 font-mono text-[11px] flex items-center gap-2 hover:bg-[#1a1a1a] transition-colors ${
                  i === 0 ? "border-[#FFD700] text-[#FFD700]" : "border-[#3a3a3a] text-[#e2e2e2]"
                }`}
                title={`${b.count} tip${b.count === 1 ? "" : "s"} · first ${ago(b.first_ts)}`}
              >
                {i === 0 && <Crown size={11} fill="currentColor" />}
                {short(b.wallet)} <span className="text-white">{b.sol} SOL</span>
              </a>
            ))}
          </div>
          <ul className="mt-4 divide-y divide-[#2a2a2a] font-mono text-[11px]" data-testid="backer-recent">
            {data.recent.map((r, i) => (
              <li key={`${r.wallet}-${r.ts}-${i}`} className="py-1.5 flex items-center justify-between text-[#d0d0d0]">
                <span><span className="text-[#e2e2e2]">{short(r.wallet)}</span> backed with <span className="text-[#00ff41]">{r.sol} SOL</span></span>
                <span>{ago(r.ts)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
